/*!
 * Advanced Image Optimization System - Stickerine Theme
 * Handles lazy loading, WebP conversion, progressive loading, and optimization
 * Version: 1.0.0
 */

window.StickerineImageOptimizer = {
  
  config: {
    lazyLoadMargin: '50px',
    webpSupported: null,
    intersectionThreshold: 0.1,
    maxRetries: 3,
    retryDelay: 1000,
    preloadCritical: true,
    optimizedSizes: [200, 300, 400, 600, 800, 1200]
  },

  observers: {
    lazy: null,
    critical: null
  },

  metrics: {
    imagesLoaded: 0,
    totalLoadTime: 0,
    webpUsage: 0,
    failures: 0
  },

  /**
   * Initialize image optimization system
   */
  initialize() {
    console.log('🖼️ Initializing Stickerine Image Optimization System...');
    
    this.detectWebPSupport();
    this.setupLazyLoading();
    this.optimizeExistingImages();
    this.preloadCriticalImages();
    
    console.log('✅ Image optimization system active');
    console.log(`📊 WebP supported: ${this.config.webpSupported}`);
  },

  /**
   * Detect WebP support
   */
  detectWebPSupport() {
    if (this.config.webpSupported !== null) return;

    const webp = new Image();
    webp.onload = webp.onerror = () => {
      this.config.webpSupported = (webp.height === 2);
    };
    webp.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
  },

  /**
   * Setup lazy loading with optimization
   */
  setupLazyLoading() {
    if (!window.IntersectionObserver) return;

    this.observers.lazy = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadImageOptimized(entry.target);
          this.observers.lazy.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: this.config.lazyLoadMargin,
      threshold: this.config.intersectionThreshold
    });

    // Observe all images with data-src
    this.observeLazyImages();
  },

  /**
   * Observe lazy loading images
   */
  observeLazyImages() {
    const lazyImages = document.querySelectorAll('img[data-src], img[loading="lazy"]');
    
    lazyImages.forEach(img => {
      if (!img.src || img.src.includes('data:')) {
        this.observers.lazy.observe(img);
      }
    });

    console.log(`🔍 Observing ${lazyImages.length} lazy load images`);
  },

  /**
   * Load image with optimization
   */
  async loadImageOptimized(img) {
    const startTime = performance.now();
    let attempt = 0;

    const loadWithRetry = async () => {
      attempt++;
      
      try {
        const optimizedSrc = this.optimizeImageUrl(img.dataset.src || img.src, img);
        
        // Create new image for preloading
        const preloader = new Image();
        
        return new Promise((resolve, reject) => {
          preloader.onload = () => {
            img.src = optimizedSrc;
            img.classList.add('loaded');
            
            const loadTime = performance.now() - startTime;
            this.recordImageLoad(optimizedSrc, loadTime, true);
            
            resolve();
          };
          
          preloader.onerror = () => {
            reject(new Error(`Failed to load: ${optimizedSrc}`));
          };
          
          preloader.src = optimizedSrc;
        });
        
      } catch (error) {
        if (attempt < this.config.maxRetries) {
          console.warn(`⚠️ Image load attempt ${attempt} failed, retrying...`);
          await this.delay(this.config.retryDelay);
          return loadWithRetry();
        } else {
          console.error('❌ Image loading failed after all retries:', error);
          this.recordImageLoad(img.src, performance.now() - startTime, false);
        }
      }
    };

    await loadWithRetry();
  },

  /**
   * Optimize image URL with WebP and sizing
   */
  optimizeImageUrl(src, imgElement) {
    if (!src || !src.includes('shopify')) return src;

    // Get optimal size based on container
    const containerWidth = imgElement.offsetWidth || imgElement.parentElement?.offsetWidth || 400;
    const optimalSize = this.getOptimalSize(containerWidth);
    
    // Build optimized URL
    let optimizedUrl = src;
    
    // Add size parameter
    if (!src.includes('width=')) {
      const separator = src.includes('?') ? '&' : '?';
      optimizedUrl += `${separator}width=${optimalSize}`;
    }
    
    // Add WebP format if supported
    if (this.config.webpSupported && !src.includes('format=')) {
      const separator = optimizedUrl.includes('?') ? '&' : '?';
      optimizedUrl += `${separator}format=webp`;
      this.metrics.webpUsage++;
    }

    return optimizedUrl;
  },

  /**
   * Get optimal image size
   */
  getOptimalSize(containerWidth) {
    // Account for high DPI displays
    const targetWidth = containerWidth * (window.devicePixelRatio || 1);
    
    // Find the smallest size that's larger than target
    return this.config.optimizedSizes.find(size => size >= targetWidth) || 
           this.config.optimizedSizes[this.config.optimizedSizes.length - 1];
  },

  /**
   * Optimize existing images
   */
  optimizeExistingImages() {
    const existingImages = document.querySelectorAll('img[src]:not([data-src])');
    let optimized = 0;
    
    existingImages.forEach(img => {
      if (img.src && img.src.includes('shopify')) {
        const originalSrc = img.src;
        const optimizedSrc = this.optimizeImageUrl(originalSrc, img);
        
        if (optimizedSrc !== originalSrc) {
          img.src = optimizedSrc;
          optimized++;
        }
      }
    });

    if (optimized > 0) {
      console.log(`🔧 Optimized ${optimized} existing images`);
    }
  },

  /**
   * Preload critical images
   */
  preloadCriticalImages() {
    if (!this.config.preloadCritical) return;

    // Hero stickers
    const heroStickers = document.querySelectorAll('.hero-sticker img');
    heroStickers.forEach((img, index) => {
      if (index < 3) { // Only preload first 3 stickers
        this.createPreloadLink(img.src, index === 0 ? 'high' : 'low');
      }
    });

    // First deal card
    const firstDealImage = document.querySelector('.deal-card:first-child .deal-image img');
    if (firstDealImage) {
      this.createPreloadLink(firstDealImage.src, 'low');
    }
  },

  /**
   * Create preload link
   */
  createPreloadLink(src, priority = 'low') {
    if (!src) return;

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = this.optimizeImageUrl(src, { offsetWidth: 200 });
    link.fetchpriority = priority;
    
    document.head.appendChild(link);
  },

  /**
   * Record image loading metrics
   */
  recordImageLoad(src, loadTime, success) {
    this.metrics.imagesLoaded++;
    this.metrics.totalLoadTime += loadTime;
    
    if (!success) {
      this.metrics.failures++;
    }
    
    // Report to performance dashboard if available
    if (window.StickerinePerformanceDashboard) {
      window.StickerinePerformanceDashboard.metrics.images.push({
        src: src.split('/').pop(), // Just filename for privacy
        loadTime,
        success,
        timestamp: Date.now()
      });
    }
  },

  /**
   * Get optimization statistics
   */
  getStats() {
    const avgLoadTime = this.metrics.imagesLoaded > 0 ? 
                       this.metrics.totalLoadTime / this.metrics.imagesLoaded : 0;
    
    return {
      imagesLoaded: this.metrics.imagesLoaded,
      averageLoadTime: avgLoadTime.toFixed(2) + 'ms',
      webpUsage: this.metrics.webpUsage,
      webpSupported: this.config.webpSupported,
      failureRate: (this.metrics.failures / Math.max(this.metrics.imagesLoaded, 1) * 100).toFixed(1) + '%',
      optimizationActive: true
    };
  },

  /**
   * Force optimize all images
   */
  optimizeAll() {
    console.log('🚀 Force optimizing all images...');
    
    this.optimizeExistingImages();
    this.observeLazyImages();
    this.preloadCriticalImages();
    
    console.log('✅ All images optimized');
    return this.getStats();
  },

  /**
   * Utility delay function
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
};

// Make available globally
window.optimizeImages = () => window.StickerineImageOptimizer.optimizeAll();

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.StickerineImageOptimizer.initialize();
  });
} else {
  window.StickerineImageOptimizer.initialize();
}